import type { ReactNode } from 'react';
import LayoutAuth from '@/layouts/LayoutAuth';

// Páginas públicas (auth layout)
import PageLogin from '@/pages/auth/PageLogin';
import PageForgotPassword from '@/pages/auth/PageForgotPassword';

// Páginas do app (sidebar layout)
import PageHome from '@/pages/PageHome';
import PagePerfil from '@/pages/PagePerfil';
import PageCentralAjuda from '@/pages/PageCentralAjuda';
import PageDownloads from '@/pages/analytics/PageDownloads';
import PageAgentesNocivos from '@/pages/saude-seguranca/PageAgentesNocivos';
import PageTrabalhadorAfastamento from '@/pages/trabalhador/PageTrabalhadorAfastamento';
import PageTrabalhadorCBO from '@/pages/trabalhador/PageTrabalhadorCBO';
import PageTrabalhadorCboAnalise from '@/pages/trabalhador/analise/PageTrabalhadorCboAnalise';
import PageTrabalhadorHorarios from '@/pages/trabalhador/PageTrabalhadorHorarios';
import PageTrabalhadorHorariosDetalhe from '@/pages/trabalhador/PageTrabalhadorHorariosDetalhe';
import PageRubricas from '@/pages/folha-pagamento/index/PageRubricas';
import PageIncidenciaNatureza from '@/pages/esocial/rubricas/PageIncidenciaNatureza';
import PageClassificacaoAnalise, {
  CONFIG_CBO,
  CONFIG_HORARIOS,
} from '@/pages/esocial/rubricas/PageClassificacaoAnalise';
import PageCboOcupacoes from '@/pages/esocial/PageCboOcupacoes';
import PageCertidaoFederal from '@/pages/certidoes/PageCertidaoFederal';
import PageCrf from '@/pages/certidoes/PageCrf';
import PageCndt from '@/pages/certidoes/PageCndt';
import PageDctf from '@/pages/encargos/PageDctf';
import PageMensagensEcac from '@/pages/encargos/PageMensagensEcac';
import PageCessao from '@/pages/egov/PageCessao';
// Central de Reprocessamento: versão com LÓGICA REAL (Fase 1) — lista + detalhes.
import PageCentralReprocessamento from '@/pages/central-reprocessamento/PageCentralReprocessamento';
import PageCentralReprocessamentoDetalhes from '@/pages/central-reprocessamento/PageCentralReprocessamentoDetalhes';
import PageCentralReprocessamentoHistorico from '@/pages/central-reprocessamento/PageCentralReprocessamentoHistorico';
import PageEmpresas from '@/pages/admin/PageEmpresas';
import PageUsuarios from '@/pages/admin/PageUsuarios';
import PagePerfisPermissao from '@/pages/admin/PagePerfisPermissao';
import PageImportacao from '@/pages/admin/PageImportacao';
import PageTabelas from '@/pages/admin/PageTabelas';
import PageTabelaDetalhe from '@/pages/admin/tabelas/PageTabelaDetalhe';
import PageParametrosAnalise from '@/pages/admin/PageParametrosAnalise';
import PageAuditoria from '@/pages/admin/PageAuditoria';
import PageDocumentos from '@/pages/admin/PageDocumentos';

import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { PageRubricasAnalise } from '@/pages/folha-pagamento/analise/PageRubricasAnalise';
import { PageRubricasAcaoEmMassa } from '@/pages/folha-pagamento/acao-em-massa/PageRubricasAcaoEmMassa';

export interface AppRoute {
  path: string;
  element: ReactNode;
  /**
   * Código(s) de permissão (`egov:...`) exigido(s) para a rota. `hasAnyPermission`
   * decide o acesso (basta 1 do array). Ausente = só exige AUTENTICAÇÃO (Home,
   * Perfil, Ajuda e telas ainda sem código mapeado).
   */
  permission?: string | string[];
}

// Rotas públicas — renderizadas sob <LayoutAuth />, nunca guardadas.
export const AUTH_LAYOUT = LayoutAuth;
export const PUBLIC_ROUTES: AppRoute[] = [
  { path: '/login', element: <PageLogin /> },
  ...(import.meta.env.VITE_TENANT_IN_PATH === 'true'
    ? [{ path: '/:tenant_slug/login', element: <PageLogin /> }]
    : []),
  { path: '/login/sso', element: <PageLogin /> },
  { path: '/recuperar-senha', element: <PageForgotPassword /> },
];

// Rotas do app — sob <LayoutMain /> (+ RequireAuth quando os guards ligam).
// Cada `permission` vem de MENU_PERMISSIONS (mapa único tela→código, CON-4960).
export const APP_ROUTES: AppRoute[] = [
  // Geral (só auth)
  { path: '/home', element: <PageHome /> },
  { path: '/perfil', element: <PagePerfil /> },
  { path: '/central-de-ajuda', element: <PageCentralAjuda /> },

  // Exportações
  {
    path: '/analytics/downloads',
    element: <PageDownloads />,
    permission: MENU_PERMISSIONS.EXPORTACOES,
  },

  // Saúde e segurança
  {
    path: '/saude-seguranca/agentes-nocivos',
    element: <PageAgentesNocivos />,
    permission: MENU_PERMISSIONS.SAUDE_SEGURANCA_AGENTES_NOCIVOS_APOSENTADORIA_ESPECIAL,
  },

  // Folha de pagamento (eSocial)
  {
    path: '/esocial/rubricas',
    element: <PageRubricas />,
    permission: MENU_PERMISSIONS.FOLHA_DE_PAGAMENTO,
  },
  {
    path: '/esocial/rubricas/analise/:id',
    element: <PageRubricasAnalise />,
    permission: MENU_PERMISSIONS.FOLHA_DE_PAGAMENTO,
  },
  {
    path: '/esocial/rubricas/acao-em-massa',
    element: <PageRubricasAcaoEmMassa />,
    permission: MENU_PERMISSIONS.FOLHA_DE_PAGAMENTO_EDITAR,
  },
  {
    path: '/esocial/rubricas/incidencias-por-natureza',
    element: <PageIncidenciaNatureza />,
    permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
  },
  {
    path: '/esocial/rubricas/classificacao-de-analise',
    element: <PageClassificacaoAnalise />,
    permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
  },
  {
    // listagem oficial de ocupações do CBO (mtecbo) — alvo do olho da Tabela de CBO (CON-4866)
    path: '/esocial/cbo/ocupacoes',
    element: <PageCboOcupacoes />,
    permission: MENU_PERMISSIONS.CONFIG_TABELAS_READ,
  },
  {
    path: '/esocial/cbo/classificacao-de-analise',
    element: <PageClassificacaoAnalise config={CONFIG_CBO} />,
    permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
  },
  {
    path: '/esocial/horarios/classificacao-de-analise',
    element: <PageClassificacaoAnalise config={CONFIG_HORARIOS} />,
    permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
  },

  // Trabalhador
  {
    path: '/esocial/trabalhador/afastamento',
    element: <PageTrabalhadorAfastamento />,
    permission: MENU_PERMISSIONS.AFASTADOS_E_INATIVOS,
  },
  {
    path: '/esocial/trabalhador/cbo',
    element: <PageTrabalhadorCBO />,
    permission: MENU_PERMISSIONS.CBO,
  },
  {
    path: '/esocial/trabalhador/cbo/analise/:id',
    element: <PageTrabalhadorCboAnalise />,
    permission: MENU_PERMISSIONS.CBO,
  },
  {
    path: '/esocial/trabalhador/horarios',
    element: <PageTrabalhadorHorarios />,
    permission: MENU_PERMISSIONS.HORARIOS_E_JORNADA,
  },
  {
    path: '/esocial/trabalhador/horarios/:analiseId',
    element: <PageTrabalhadorHorariosDetalhe />,
    permission: MENU_PERMISSIONS.HORARIOS_E_JORNADA,
  },

  // Certidões
  {
    path: '/certidoes/federal',
    element: <PageCertidaoFederal />,
    permission: MENU_PERMISSIONS.CERTIDAO_FEDERAL,
  },
  { path: '/certidoes/crf', element: <PageCrf />, permission: MENU_PERMISSIONS.CRF },
  { path: '/certidoes/cndt', element: <PageCndt />, permission: MENU_PERMISSIONS.CNDT },

  // Encargos
  { path: '/encargos/dctf', element: <PageDctf />, permission: MENU_PERMISSIONS.DCTF },
  {
    path: '/encargos/mensagens-ecac',
    element: <PageMensagensEcac />,
    permission: MENU_PERMISSIONS.MENSAGENS_ECAC,
  },

  // eGov
  { path: '/egov/cessao', element: <PageCessao />, permission: MENU_PERMISSIONS.CESSAO },

  // Central de Reprocessamento (Fase 1 — lógica real)
  {
    path: '/central-de-reprocessamento',
    element: <PageCentralReprocessamento />,
    permission: MENU_PERMISSIONS.CENTRAL_REPROCESSAMENTO,
  },
  {
    path: '/central-de-reprocessamento/historico/:nrinsc',
    element: <PageCentralReprocessamentoHistorico />,
    permission: MENU_PERMISSIONS.CENTRAL_REPROCESSAMENTO,
  },
  {
    path: '/central-de-reprocessamento/:id',
    element: <PageCentralReprocessamentoDetalhes />,
    permission: MENU_PERMISSIONS.CENTRAL_REPROCESSAMENTO,
  },

  // Administração (config — split :read/:write, CON-4960)
  {
    path: '/admin/empresas',
    element: <PageEmpresas />,
    permission: MENU_PERMISSIONS.CONFIG_EMPRESAS_READ,
  },
  {
    path: '/admin/usuarios',
    element: <PageUsuarios />,
    permission: MENU_PERMISSIONS.CONFIG_USUARIOS_READ,
  },
  {
    path: '/admin/perfis',
    element: <PagePerfisPermissao />,
    permission: MENU_PERMISSIONS.CONFIG_PERFIS_READ,
  },
  {
    path: '/admin/importacao',
    element: <PageImportacao />,
    permission: [
      MENU_PERMISSIONS.CONFIG_ESOCIAL_IMPORTACAO_READ,
      MENU_PERMISSIONS.CONFIG_ESOCIAL_IMPORTACAO_WRITE,
    ],
  },
  {
    path: '/admin/tabelas',
    element: <PageTabelas />,
    permission: MENU_PERMISSIONS.CONFIG_TABELAS_READ,
  },
  {
    path: '/admin/tabelas/:tabela',
    element: <PageTabelaDetalhe />,
    permission: MENU_PERMISSIONS.CONFIG_TABELAS_READ,
  },
  {
    path: '/admin/parametros',
    element: <PageParametrosAnalise />,
    permission: MENU_PERMISSIONS.CONFIG_PARAMETROS_ANALISE_READ,
  },
  {
    path: '/admin/auditoria',
    element: <PageAuditoria />,
    permission: MENU_PERMISSIONS.CONFIG_AUDITORIA_READ,
  },
  {
    path: '/admin/documentos',
    element: <PageDocumentos />,
    permission: MENU_PERMISSIONS.CONFIG_DOCUMENTOS_READ,
  },
];
